"use client";

import { useEffect } from "react";
import { Facebook, Instagram, Leaf, Twitter, Youtube } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="flex flex-col gap-y-3">
        {[0, 1, 2, 3, 4].map(i => <li key={i} data-index={i}>
            <div className="h-4 w-32 animate-pulse rounded-md bg-[hsl(240_10%_15%)]" />
          </li>)}
      </ul>;
  }
  return <ul className="flex flex-col gap-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-[hsl(240_5%_65%)] transition-colors duration-300 hover:text-[var(--accent-deep-red)]">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
const learnLinks = [{
  label: "Services",
  to: "/Services"
}, {
  label: "About Us",
  to: "/About"
}, {
  label: "FAQ",
  to: "/FAQ"
}];
const socialLinks = [{
  label: "Instagram",
  href: "https://instagram.com/clearline",
  Icon: Instagram
}, {
  label: "YouTube",
  href: "https://youtube.com/clearline",
  Icon: Youtube
}, {
  label: "Facebook",
  href: "https://facebook.com/clearline",
  Icon: Facebook
}, {
  label: "Twitter",
  href: "https://twitter.com/clearline",
  Icon: Twitter
}];
export default function () {
  useEffect(() => {
    const existingScript = document.body.querySelector('script[data-widget-id="6aac0c58adf66fe3c7ffc448"], script[src="https://widgets.leadconnectorhq.com/loader.js"]');
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://widgets.leadconnectorhq.com/loader.js";
      script.setAttribute("data-resources-url", "https://widgets.leadconnectorhq.com/chat-widget/loader.js");
      script.setAttribute("data-widget-id", "6aac0c58adf66fe3c7ffc448");
      document.body.appendChild(script);
    }
  }, []);
  return <section data-section-id="14169" id="site-footer" className="bg-[hsl(240_10%_4%)] py-20 px-6 md:px-10 lg:px-16">
      <div className="max-w-[1280px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-12 md:gap-x-8">
          {}
          <div className="md:col-span-5">
            <Link to="/" className="inline-block">
              <WvcLogo className="h-8 w-auto" />
            </Link>
            <p className="mt-6 text-base leading-[1.65] text-[hsl(240_5%_65%)] max-w-md" style={{
            fontFamily: "var(--font-sans)"
          }}>
              Positioning your business as the <span className="italic text-[hsl(0_0%_98%)]">undisputed authority</span> in the age of AI search. We clean, track, and optimize your digital footprint for maximum visibility.
            </p>

            <div className="mt-8 flex items-center gap-3">
              {socialLinks.map((social, i) => {
              const Icon = social.Icon;
              return <Link key={social.label} data-index={i} to={social.href} newTab aria-label={social.label} className="group flex h-10 w-10 items-center justify-center rounded-lg border border-[hsl(240_5%_15%)] text-[hsl(240_5%_65%)] transition-all duration-[250ms] ease-out hover:border-[var(--accent-deep-red)] hover:bg-[var(--accent-deep-red)] hover:text-[hsl(0_0%_98%)]">
                    <Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
                  </Link>;
            })}
            </div>
          </div>

          {}
          <div className="md:col-span-3">
            <h4 className="font-default text-base font-semibold tracking-[0.01em] text-[hsl(0_0%_98%)]">
              Navigation
            </h4>
            <div className="mt-5">
              <MenuProvider menu_id="17">
                <FooterMenuConsumer />
              </MenuProvider>
            </div>
          </div>

          {}
          <div className="md:col-span-2">
            <h4 className="font-default text-base font-semibold tracking-[0.01em] text-[hsl(0_0%_98%)]">
              Resources
            </h4>
            <ul className="mt-5 flex flex-col gap-y-3">
              {learnLinks.map((item, i) => <li key={item.label} data-index={i}>
                  <Link to={item.to} className="text-sm text-[hsl(240_5%_65%)] transition-colors duration-300 hover:text-[var(--accent-deep-red)]">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="md:col-span-2">
            <h4 className="font-default text-base font-semibold tracking-[0.01em] text-[hsl(0_0%_98%)]">
              Contact
            </h4>
            <ul className="mt-5 flex flex-col gap-y-3 text-sm">
              <li>
                <Link to="mailto:hello@clearline.ai" className="text-[var(--accent-deep-red)] transition-colors duration-300 hover:opacity-80">Clearlinegeo@gmail.com</Link>
              </li>
              <li>
                <Link to="tel:+14692736808" className="text-[hsl(240_5%_65%)] transition-colors duration-300 hover:text-[var(--accent-deep-red)]">469-273-6808</Link>
              </li>
              <li>
                <span className="text-[hsl(240_5%_65%)] leading-[1.5] block">
                  Mon–Fri 9am–6pm
                </span>
              </li>
            </ul>
          </div>
        </div>

        {}
        <Separator className="mt-16 bg-[hsl(240_5%_15%)]" />

        {}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 items-center gap-y-4">
          <p className="text-xs tracking-[0.01em] text-[hsl(240_5%_65%)] md:text-left text-center">
            © {new Date().getFullYear()} ClearLine · Precision AI Visibility
          </p>
          <div className="flex justify-center">
            <Leaf className="h-4 w-4 text-[var(--accent-deep-red)]" strokeWidth={1.5} aria-hidden="true" />
          </div>
          <p className="text-xs tracking-[0.01em] text-[hsl(240_5%_65%)] md:text-right text-center">
            ClearLine GEO is operated by Clearline Works LLC.
          </p>
        </div>
      </div>
    </section>;
}